/**
 * Mood Brew – Recharge Bundle Widget Enhancements
 * 1) Insert a reassurance note under "Order total" on the Review step
 * 2) Hide the "$0.00" placeholder price on mobile (Step 1) without affecting real prices
 *
 * Safe guards:
 * - Won’t duplicate the note across re-renders
 * - Runs on async renders (Recharge), step changes, and dynamic updates
 * - Mobile-only rules stay mobile-only
 */
(function () {
  "use strict";

  /** -----------------------------
   *  Config
   *  ----------------------------- */
  const NOTE_TEXT =
    "Your full ritual savings, including volume savings, will be applied automatically at checkout.";

  const NOTE_ATTR = "data-moodbrew-checkout-note";
  const NOTE_ID = "moodbrew-checkout-note";
  const STYLE_ID = "moodbrew-recharge-enhancements-style";

  const MOBILE_QUERY = "(max-width: 768px)";

  /** -----------------------------
   *  Utilities
   *  ----------------------------- */
  function isMobile() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
  }

  function normalizeText(str) {
    return (str || "").replace(/\s+/g, " ").trim();
  }

  function findLeafByExactText(text) {
    const target = normalizeText(text);
    return Array.from(document.querySelectorAll("*")).find(
      (el) => el.children.length === 0 && normalizeText(el.textContent) === target
    );
  }

  function ensureStylesInjected() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      /* Mood Brew – Recharge widget enhancements */

      /* Reassurance note under Order total */
      .moodbrew-checkout-note {
        display: block;
        margin-top: 10px;
        margin-bottom: 14px;
        padding: 0;
        line-height: 1.45;
        font-size: 14px;
        opacity: 0.85;
        text-align: center;
        max-width: 560px;
      }

      /* Mobile tightening (keeps note attached to Order total, reduces dead space) */
      @media ${MOBILE_QUERY} {
        .moodbrew-checkout-note {
          margin-top: 6px !important;
          margin-bottom: 10px !important;
          font-size: 13.5px !important;
          line-height: 1.4 !important;
          max-width: 90% !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /** -----------------------------
   *  1) Insert note under Order total (Review step)
   *  ----------------------------- */
  function insertNoteUnderOrderTotal() {
    // Find the "Order total" label
    const orderTotalLabel = findLeafByExactText("Order total");
    if (!orderTotalLabel) return false;

    const row = orderTotalLabel.closest("div") || orderTotalLabel.parentElement;
    if (!row) return false;

    // Guard: If we already inserted, do nothing
    const existing = document.getElementById(NOTE_ID) || document.querySelector(`[${NOTE_ATTR}="true"]`);
    if (existing) {
      // If the note exists but got moved, ensure it stays right after the row
      if (existing.previousElementSibling !== row) {
        row.after(existing);
      }
      // Keep text synced
      if (existing.textContent !== NOTE_TEXT) existing.textContent = NOTE_TEXT;
      return true;
    }

    ensureStylesInjected();

    const note = document.createElement("div");
    note.id = NOTE_ID;
    note.setAttribute(NOTE_ATTR, "true");
    note.className = "moodbrew-checkout-note";
    note.textContent = NOTE_TEXT;

    row.after(note);
    return true;
  }

  /** -----------------------------
   *  2) Hide "$0.00" placeholder price on mobile
   *  ----------------------------- */
  function hideZeroPriceOnMobile() {
    if (!isMobile()) return;

    // Limit to Recharge bundle widget if present
    const root = document.querySelector(".rc-bundle-builder") || document;

    // Keep selectors reasonably broad, but avoid hiding real line-item prices by:
    // - only hiding nodes that contain "$0.00"
    // - and only if they look like widget totals/summary/price blocks
    const candidates = root.querySelectorAll(
      [
        ".rc-bundle-builder__price",
        ".rc-bundle-builder__summary-price",
        "[data-testid*='price']",
        "[class*='summary'] [class*='price']",
        "[class*='bundle'] [class*='price']",
      ].join(",")
    );

    candidates.forEach((el) => {
      const txt = normalizeText(el.textContent);
      if (!txt) return;

      // Only hide the common placeholder state
      // (We intentionally do NOT hide if other amounts are present)
      const containsZero = txt.includes("$0.00");

      // If the element includes any other dollar amount, leave it alone
      const otherAmounts = txt.match(/\$\s*\d+(?:,\d{3})*(?:\.\d{2})?/g) || [];
      const hasNonZero = otherAmounts.some((m) => normalizeText(m) !== "$0.00");

      if (containsZero && !hasNonZero) {
        el.style.display = "none";
      }
    });
  }

  /** -----------------------------
   *  Orchestration
   *  ----------------------------- */
  function runAll() {
    insertNoteUnderOrderTotal();
    hideZeroPriceOnMobile();
  }

  // Initial run + async re-renders
  ensureStylesInjected();
  runAll();

  // Recharge renders async; keep trying briefly
  let tries = 0;
  const maxTries = 80; // ~24s
  const timer = setInterval(() => {
    tries += 1;
    runAll();
    // Stop early once the note is placed (most expensive check)
    const done = !!document.querySelector(`[${NOTE_ATTR}="true"]`);
    if (done || tries >= maxTries) clearInterval(timer);
  }, 300);

  // Step changes / quantity changes often happen on click
  document.addEventListener(
    "click",
    () => {
      setTimeout(runAll, 250);
    },
    { passive: true }
  );

  // Also watch for async DOM changes (Recharge re-renders components)
  const observer = new MutationObserver(() => {
    runAll();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
